import React, { useEffect, useState } from "react";
import Moment from "react-moment";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import XGSIcon from "../../../ui-components/icon/xgsIcon";
import XGSIcons from "../../../ui-components/icon/xgsIcons";
import Amount from "../../../ui-components/molecules/amount/amount";
import Tag from "../../../ui-components/molecules/tag/tag";
import PdfActionSplitButton from "../../../ui-components/pdf-action-split-button/pdfActionSplitButton";
import PaymentService from "../../../app/data/payment/paymentService";
import { PaymentDetail, PaymentEventItem } from "../../../app/data/payment/PaymentSession";
import { PdfDocumentAction } from "../../../app/data/shipping-docs/pdfDocumentActionStorage";
import { deliverPdf, getPdfDownloadErrorMessage } from "../../../app/data/shipping-docs/downloadPdf";
import { copyTextToClipboard } from "../../../hooks/utils";
import { PAYMENT_PROVIDER_OPTIONS, paymentChannelColor, paymentChannelLabel, statusColor } from "./paymentAdminUtils";

const paymentService = PaymentService.getInstance();

export interface PaymentDetailSidebarProps {
  show: boolean;
  isLoading: boolean;
  detail: PaymentDetail | null;
  detailError: string | null;
  onClose: () => void;
}

const text = (value?: string | number | null) =>
  value == null || value === "" ? "—" : String(value);

const money = (cents?: number | null) =>
  cents == null ? "—" : <Amount amount={cents / 100} />;

const when = (value?: string) =>
  value ? <Moment format="MM/DD/YYYY h:mm:ss A">{value}</Moment> : "—";

const STATUS_TITLE: Record<string, string> = {
  COMPLETED: "Payment successful",
  FAILED: "Payment not completed",
  CANCELLED: "Payment cancelled",
  EXPIRED: "Payment expired",
  PENDING: "Payment in progress",
  CREATED: "Payment started",
};

const providerLabel = (provider?: string) =>
  PAYMENT_PROVIDER_OPTIONS.find((option) => option.value === provider)?.label || provider || "—";

const CopyButton: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <button
    type="button"
    className="xgs-payment-admin__copy"
    aria-label={`Copy ${label}`}
    onClick={() => copyTextToClipboard(value)}
  >
    <XGSIcon icon={XGSIcons.faCopy} />
  </button>
);

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="xgs-payment-admin__card">
    <h3>{title}</h3>
    {children}
  </section>
);

const Row: React.FC<{ label: string; children: React.ReactNode; total?: boolean }> = ({
  label,
  children,
  total,
}) => (
  <div className={`xgs-payment-admin__row${total ? " xgs-payment-admin__row--total" : ""}`}>
    <span>{label}</span>
    <span>{children}</span>
  </div>
);

const IdRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <div className="xgs-payment-admin__id-row">
    <span className="xgs-payment-admin__id-label">{label}</span>
    <span className="xgs-payment-admin__copy-line">
      {text(value)}
      {value ? <CopyButton label={label} value={value} /> : null}
    </span>
  </div>
);

const PaymentDetailSidebar: React.FC<PaymentDetailSidebarProps> = ({
  show,
  isLoading,
  detail,
  detailError,
  onClose,
}) => {
  const [expandedPayloads, setExpandedPayloads] = useState<Record<string, boolean>>({});
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const payment = detail?.payment;

  useEffect(() => {
    setPdfError(null);
    setPdfBusy(false);
    setExpandedPayloads({});
  }, [payment?.paymentId]);

  const onPdfAction = async (action: PdfDocumentAction) => {
    if (!payment?.paymentId) return;
    setPdfBusy(true);
    setPdfError(null);
    try {
      const res = await paymentService.getPaymentReceiptPdf(payment.paymentId);
      if (!res.ok() || !res.data) {
        throw new Error(
          typeof res.getError?.() === "string" ? (res.getError() as string) : "Failed to download receipt PDF"
        );
      }
      deliverPdf(res.data, `payment-receipt-${payment.paymentId}.pdf`, action);
    } catch (e) {
      setPdfError(getPdfDownloadErrorMessage(e, "Failed to download receipt PDF"));
    } finally {
      setPdfBusy(false);
    }
  };

  const invoiceLines: { invoiceNumber: number; amountCents?: number }[] =
    payment?.invoices && payment.invoices.length
      ? payment.invoices
      : (payment?.invoiceNumbers || []).map((invoiceNumber) => ({ invoiceNumber }));

  const totalChargedCents =
    payment?.amountCents != null || payment?.surchargeAmountCents != null
      ? (payment?.amountCents || 0) + (payment?.surchargeAmountCents || 0)
      : null;

  const succeeded = payment?.status === "COMPLETED";
  const quiet = payment?.status === "FAILED" || payment?.status === "CANCELLED" || payment?.status === "EXPIRED";

  return (
    <SlideOutSidebar
      show={show}
      onClose={onClose}
      header="Payment detail"
      size="medium-860"
      spinner={isLoading}
    >
      {detailError && <XGSErrorMessage>{detailError}</XGSErrorMessage>}
      {payment && (
        <div className="xgs-payment-admin__detail">
          <div className="xgs-payment-admin__result-head">
            <span
              className={`xgs-payment-admin__result-mark${
                quiet
                  ? " xgs-payment-admin__result-mark--quiet"
                  : succeeded
                  ? ""
                  : " xgs-payment-admin__result-mark--pending"
              }`}
              aria-hidden
            >
              <XGSIcon icon={succeeded ? XGSIcons.faCheck : quiet ? XGSIcons.faTimes : XGSIcons.faClock} />
            </span>
            <div>
              <h2>{STATUS_TITLE[payment.status] || "Payment status"}</h2>
              <p>
                <Tag mods={{ color: statusColor(payment.status) }}>{payment.status}</Tag>
                <Tag mods={{ color: paymentChannelColor(payment.paymentChannel) }}>
                  {paymentChannelLabel(payment.paymentChannel)}
                </Tag>
              </p>
            </div>
          </div>
          {succeeded && (
            <div className="xgs-payment-admin__receipt">
              <span className="xgs-payment-admin__receipt-icon" aria-hidden>
                <XGSIcon icon={XGSIcons.faFileAlt} />
              </span>
              <span className="xgs-payment-admin__receipt-copy">
                <span className="xgs-payment-admin__receipt-title">
                  {pdfBusy ? "Preparing receipt…" : "Receipt"}
                </span>
                <span className="xgs-payment-admin__receipt-hint">Print or download the PDF</span>
              </span>
              <PdfActionSplitButton onAction={onPdfAction} spinner={pdfBusy}>
                Receipt
              </PdfActionSplitButton>
            </div>
          )}
          {pdfError && <XGSErrorMessage>{pdfError}</XGSErrorMessage>}

          <Card title="Payment summary">
            <Row label="Invoice subtotal">{money(payment.amountCents)}</Row>
            <Row label="Surcharge">{money(payment.surchargeAmountCents)}</Row>
            <Row label="Surcharge rate">
              {payment.surchargePercentage != null ? `${payment.surchargePercentage}%` : "—"}
            </Row>
            <Row label="Total charged" total>{money(totalChargedCents)}</Row>
          </Card>

          <Card title="Payment details">
            <Row label="Paid via">{paymentChannelLabel(payment.paymentChannel)}</Row>
            <Row label="Account number">{text(payment.accountNumber)}</Row>
            <Row label="Account name">{text(payment.accountName)}</Row>
            <Row label="Payment method">{text(payment.paymentMethodLabel || payment.paymentMethod)}</Row>
            <Row label="Payment method code">{text(payment.paymentMethod)}</Row>
            <Row label="Provider">{providerLabel(payment.provider)}</Row>
            <Row label="Created by">{text(payment.userCreated)}</Row>
            <Row label="Updated by">{text(payment.userUpdated)}</Row>
            <Row label="Created">{when(payment.dateCreated)}</Row>
            <Row label="Updated">{when(payment.dateUpdated)}</Row>
          </Card>

          <Card title="Invoices">
            {invoiceLines.length ? (
              <>
                <div className="xgs-payment-admin__row xgs-payment-admin__row--head">
                  <span>Invoice</span>
                  <span>Amount</span>
                </div>
                {invoiceLines.map((line) => (
                  <Row key={line.invoiceNumber} label={String(line.invoiceNumber)}>
                    {money(line.amountCents)}
                  </Row>
                ))}
              </>
            ) : (
              <p className="xgs-payment-admin__detail-empty">No invoices on this payment.</p>
            )}
          </Card>

          <Card title="References">
            <IdRow label="Payment ID" value={payment.paymentId} />
            <IdRow label="Merchant reference" value={payment.merchantReference} />
            <IdRow label="External payment" value={payment.externalPaymentId} />
            <IdRow label="Provider link ID" value={payment.externalLinkId} />
            <IdRow label="Correlation ID" value={payment.correlationId} />
            <IdRow label="Idempotency key" value={payment.idempotencyKey} />
            <IdRow label="Quick Pay link ID" value={payment.publicLinkId} />
          </Card>

          <Card title="Event timeline">
            {(detail?.events || []).length ? (
              <ol className="xgs-payment-admin__detail-timeline">
                {(detail?.events || []).map((event) => (
                  <EventItem
                    key={event.id}
                    event={event}
                    expanded={!!expandedPayloads[event.id]}
                    onToggle={() =>
                      setExpandedPayloads((prev) => ({ ...prev, [event.id]: !prev[event.id] }))
                    }
                  />
                ))}
              </ol>
            ) : (
              <p className="xgs-payment-admin__detail-empty">No events recorded.</p>
            )}
          </Card>
        </div>
      )}
    </SlideOutSidebar>
  );
};

const EventItem: React.FC<{
  event: PaymentEventItem;
  expanded: boolean;
  onToggle: () => void;
}> = ({ event, expanded, onToggle }) => (
  <li className="xgs-payment-admin__detail-timeline__item">
    <div className="xgs-payment-admin__detail-timeline__time">{when(event.dateCreated)}</div>
    <div className="xgs-payment-admin__detail-timeline__card">
      <div className="xgs-payment-admin__detail-timeline__title">{text(event.eventType)}</div>
      <Row label="From">{text(event.fromStatus)}</Row>
      <Row label="To">{text(event.toStatus)}</Row>
      <Row label="Actor">{text(event.actor)}</Row>
      <IdRow label="Correlation ID" value={event.correlationId} />
      <IdRow label="Payload ref" value={event.gatewayPayloadRef} />
      {event.gatewayPayload && (
        <>
          <button type="button" className="xgs-payment-admin__payload-toggle" onClick={onToggle}>
            {expanded ? "Hide gateway payload" : "Show gateway payload"}
          </button>
          {expanded && <pre className="xgs-payment-admin__payload">{event.gatewayPayload}</pre>}
        </>
      )}
    </div>
  </li>
);

export default PaymentDetailSidebar;
